import { useFlags } from "launchdarkly-react-client-sdk";
import { memo, useMemo } from "react";
import { Control, useWatch } from "react-hook-form";

import Grid from "@mui/material/Grid";

import {
  compareAsc,
  differenceInMinutes,
  endOfWeek,
  format,
  roundToNearestMinutes
} from "date-fns";
import { usePagination } from "hooks";
import { uniq } from "lodash";

import { useAppSelector } from "app/hooks";
import { APPOINTMENT_TYPE_SCHEDULE } from "constants/appointment-types";
import { AVAILABILITY_BLOCK_COLORS_FEATURE } from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentRegion
} from "features/auth/auth-slice";
import { ExpandedSectionProvider } from "features/patients/sections/components/patient-details-section/use-expanded-section.hook";
import {
  selectExcludePersonalEvents,
  selectSelectedFromAvailableStaff,
  selectShowFromAllLocations
} from "features/scheduling/scheduler-slice";
import { useGetAppointmentTypesQuery } from "services/appointment-types";
import {
  groupByStaffMemberId,
  useGetStaffAvailabilityQuery
} from "services/staff-availability";
import { AppointmentType } from "services/types/appointment-types.type";
import {
  MemberAvailability,
  StaffAvailabilityFilters,
  StaffMemberAvailability
} from "services/types/staff-availability.type";

import { AppointmentFormValues } from "../../appointment-form.component";
import StaffAvailabilityListItems from "./staff-availability-list-items.component";

type StaffAvailabilityListProps = {
  control: Control<AppointmentFormValues, object>;
  setValue: (name: any, value: any, options?: any) => void;
  visible: boolean;
  fromDate: Date;
};

const StaffAvailabilityList = memo(
  ({ control, setValue, visible, fromDate }: StaffAvailabilityListProps) => {
    const [startTime, endTime, room, clinic, appointmentType] = useWatch({
      control,
      name: ["start_time", "end_time", "room", "clinic", "appointment_type"]
    });

    const flags = useFlags();
    const blockColorsEnabled = !!flags[AVAILABILITY_BLOCK_COLORS_FEATURE];

    const slotDurationMinutes = differenceInMinutes(
      new Date(endTime),
      new Date(startTime)
    );
    const currentRegion = useAppSelector(selectCurrentRegion);
    const currentClinic = useAppSelector(selectCurrentClinic);
    const selectedStaff = useAppSelector(selectSelectedFromAvailableStaff);
    const excludePersonalEvents = useAppSelector(selectExcludePersonalEvents);
    const showFromAllLocations = useAppSelector(selectShowFromAllLocations);

    const activeClinicId = clinic?.id;

    const selectedStaffMembers = useMemo(() => {
      const result = selectedStaff.map(({ id }) => id);
      return uniq(result);
    }, [selectedStaff]);

    const fetchParams = useMemo(
      () => ({
        staffMembersIds: selectedStaffMembers,
        currentClinicId: !showFromAllLocations
          ? activeClinicId || currentClinic?.id
          : undefined,
        slotDurationMinutes,
        roomId: room?.id,
        region: currentRegion?.id || undefined,
        excludePersonalEvents,
        appointmentTypeId: appointmentType?.id,
        limitToAppointmentBlocks:
          !!appointmentType?.limit_to_appointment_blocks,
        includeAppointmentBlockSlots: blockColorsEnabled,
        fromDate: format(
          roundToNearestMinutes(
            compareAsc(fromDate, new Date()) > 0 ? fromDate : new Date(),
            {
              nearestTo: 30,
              roundingMethod: "ceil"
            }
          ),
          "yyyy-MM-dd'T'HH:mm:ss"
        ),
        toDate: format(
          compareAsc(endOfWeek(fromDate), new Date()) > 0
            ? endOfWeek(fromDate)
            : roundToNearestMinutes(new Date(), {
                nearestTo: 30,
                roundingMethod: "ceil"
              }),
          "yyyy-MM-dd'T'HH:mm:ss"
        )
      }),
      [
        activeClinicId,
        currentClinic?.id,
        currentRegion?.id,
        excludePersonalEvents,
        fromDate,
        room?.id,
        selectedStaffMembers,
        showFromAllLocations,
        slotDurationMinutes,
        appointmentType?.id,
        appointmentType?.limit_to_appointment_blocks,
        blockColorsEnabled
      ]
    );

    const {
      data: staffAvailability = [],
      isFetching,
      onScroll: onStaffAvailabilityScroll
    } = usePagination<StaffAvailabilityFilters, StaffMemberAvailability>({
      useQuery: useGetStaffAvailabilityQuery,
      limit: 300,
      options: {
        skip:
          selectedStaffMembers.length === 0 ||
          !visible ||
          slotDurationMinutes < 1,
        refetchOnMountOrArgChange: true,
        pollingInterval: 0,
        refetchOnFocus: false,
        refetchOnReconnect: false
      },
      params: fetchParams
    });

    const normalizedStaffAvailabilityValues: MemberAvailability[] =
      useMemo(() => {
        const groupedStaffAvailability = staffAvailability.reduce(
          groupByStaffMemberId,
          {}
        );
        return selectedStaff.map((staffMember) => {
          if (groupedStaffAvailability[staffMember.id]) {
            return groupedStaffAvailability[staffMember.id];
          }
          return { staffMember, availability: [] } as MemberAvailability;
        });
      }, [staffAvailability, selectedStaff]);

    const isCollapsibleView = normalizedStaffAvailabilityValues.length > 3;

    // ENG-1434: clinic appointment types, used to (1) gate coloring — tint only when the
    // block resolves to a known clinic type — and (2) set the appointment type on slot
    // select. Args intentionally match AppointmentTypeField's query so RTK Query serves
    // both from a single cache entry (no extra request) in the common case. The BE is the
    // source of truth that only annotates clinic-bookable blocks (active, clinic-schedule,
    // non web-scheduler / non event); this map is the matching FE guardrail. Flag-gated.
    const { data: appointmentTypes } = useGetAppointmentTypesQuery(
      {
        is_active: true,
        is_clinic_schedule: true,
        exclude_type: APPOINTMENT_TYPE_SCHEDULE
      },
      { skip: !blockColorsEnabled }
    );
    const appointmentTypeById = useMemo(() => {
      const map = new Map<number, AppointmentType>();
      (appointmentTypes ?? []).forEach((type) => map.set(type.id, type));
      return map;
    }, [appointmentTypes]);

    return (
      <Grid
        container
        spacing={1}
        overflow={isCollapsibleView ? "auto" : "hidden"}
      >
        <ExpandedSectionProvider defaultSection={0}>
          <StaffAvailabilityListItems
            control={control}
            setValue={setValue}
            isCollapsibleView={isCollapsibleView}
            memberAvailability={normalizedStaffAvailabilityValues}
            onStaffAvailabilityScroll={onStaffAvailabilityScroll}
            isLoading={isFetching}
            appointmentTypeById={appointmentTypeById}
          />
        </ExpandedSectionProvider>
      </Grid>
    );
  }
);

export default StaffAvailabilityList;
